import React, { useRef, useState } from 'react';
import { FiUploadCloud, FiDownload, FiFile } from 'react-icons/fi';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { bulkUpload } from '../../utils/journalApi';
import StatusBadge from './StatusBadge';

const TEMPLATE_HEADERS = [
  'posting_key', 'gl_account', 'amount', 'cost_center',
  'profit_center', 'wbs_element', 'item_text',
];

function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Journal Template');
  XLSX.writeFile(wb, 'journal_template.xlsx');
}

export default function JournalBulkUpload() {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);

  const setSelectedFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith('.xlsx')) { toast.error('Only .xlsx files are accepted.'); return; }
    setFile(f);
    setResults(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    setSelectedFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) { toast.warn('Please select a file first.'); return; }
    setUploading(true);
    setProgress(0);
    try {
      const res = await bulkUpload(file, ev => setProgress(Math.round((ev.loaded / ev.total) * 100)));
      setResults(res.data);
      toast.success(`${res.data.length} entr${res.data.length !== 1 ? 'ies' : 'y'} processed.`);
    } catch (err) {
      if (err.response?.status === 429) toast.error('Rate limit: 10 uploads/hour exceeded.');
    }
    setUploading(false);
    setProgress(0);
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: 14 }}>
        <button className="journal-btn ghost" onClick={downloadTemplate}>
          <FiDownload size={14} /> Download Template
        </button>
      </div>

      <div className="journal-panel">
        <div className="journal-panel-head"><h3>Upload Excel File</h3></div>

        <div
          className={`journal-dropzone ${dragging ? 'dragging' : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <FiUploadCloud size={42} />
          {file ? (
            <>
              <p style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                <FiFile size={15} /> <strong>{file.name}</strong>
              </p>
              <p className="hint">Click to choose a different file</p>
            </>
          ) : (
            <>
              <p>Drag & drop your <strong>.xlsx</strong> file here</p>
              <p className="hint">or click to browse — max 10 uploads/hour</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            style={{ display:'none' }}
            onChange={e => setSelectedFile(e.target.files[0])}
          />
        </div>

        {uploading && (
          <div className="progress-wrap">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
          <button
            className="journal-btn primary"
            onClick={handleUpload}
            disabled={uploading || !file}
          >
            {uploading ? `Uploading… ${progress}%` : 'Upload & Process'}
          </button>
        </div>
      </div>

      {results && (
        <div className="journal-panel">
          <div className="journal-panel-head">
            <h3>Upload Results — {results.length} entries</h3>
          </div>
          <div className="journal-table-wrap">
            <table className="journal-table">
              <thead>
                <tr>
                  <th>Tracking ID</th><th>Status</th><th>Company</th>
                  <th>Doc Type</th><th>Currency</th><th>Posting Date</th><th>Submitted By</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.tracking_id}>
                    <td style={{ fontFamily:'monospace', fontSize:11 }}>{r.tracking_id}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>{r.company_code}</td>
                    <td>{r.document_type}</td>
                    <td>{r.currency}</td>
                    <td>{r.posting_date}</td>
                    <td>{r.submitted_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
